import { useState, useEffect, useRef } from 'react';

import api from './api';

export type TUseVehicleOptions = {
  year: string | number;
  make: string;
  model: string;
  bodyStyle: string;
  effectiveDate?: string;
  trim?: string;
  isAdpf?: boolean;
  ratingAddressRegionCode: string;
  onError: () => void;
};

function getLowerCaseValue(opt: { value?: string }) {
  return (opt?.value || '').toLowerCase();
}

export function useVehicleOptions({
  year,
  make,
  model,
  bodyStyle,
  trim,
  effectiveDate,
  isAdpf = false,
  ratingAddressRegionCode,
  onError,
}: TUseVehicleOptions) {
  const isApiV1 = ratingAddressRegionCode === 'SC';
  const yearRef = useRef<string | number | null>(null);
  const makeRef = useRef<string | null>(null);
  const modelRef = useRef<string | null>(null);
  const bodyStyleRef = useRef<string | null>(null);

  const defaultYearOpts =
    !!year && isAdpf ? [{ value: year, label: year }] : [];
  const defaultMakeOpts =
    !!make && isAdpf ? [{ value: make, label: make }] : [];
  const defaultModelOpts =
    !!model && isAdpf ? [{ value: model, label: model, styles: [] }] : [];
  const defaultBodyStyleOpts =
    !!bodyStyle && isAdpf ? [{ value: bodyStyle, label: bodyStyle }] : [];

  const defaultTrim = !!trim && isAdpf ? [{ value: trim, label: trim }] : [];

  const [yearOptions, setYearOptions] = useState(defaultYearOpts);
  const [makeOptions, setMakeOptions] = useState(defaultMakeOpts);
  const [modelOptions, setModelOptions] = useState<
    {
      label: string;
      value: string;
      styles:
        | string[]
        | {
            style: string;
            trims: string[];
          }[];
    }[]
  >(defaultModelOpts);
  const [bodyStyleOptions, setBodyStyleOptions] =
    useState(defaultBodyStyleOpts);
  const [trimOptions, setTrimOptions] = useState(defaultTrim);

  useEffect(
    function fetchYears() {
      let isActive = true;
      if (!yearOptions.length && !isAdpf) {
        api
          .getVehiclesYears(ratingAddressRegionCode, effectiveDate)
          .then((years: number[]) => {
            if (isActive) {
              setYearOptions(
                years.map((year) => ({ value: year, label: year }))
              );
            }
          })
          .catch(onError);
      }
      return () => (isActive = false);
    } as () => void,
    [isAdpf, onError, ratingAddressRegionCode, yearOptions.length]
  );

  useEffect(
    function fetchMakes() {
      let isActive = true;
      if (
        yearOptions.map((opt) => opt.value).includes(year) &&
        yearRef.current !== year &&
        !isAdpf
      ) {
        yearRef.current = year;
        api
          .getVehiclesMakes(year, ratingAddressRegionCode, effectiveDate)
          .then((makes: string[]) => {
            if (isActive) {
              setMakeOptions(
                makes.map((make) => ({ label: make, value: make }))
              );
            }
          })
          .catch(onError);
      }
      return () => (isActive = false);
    } as () => void,
    [isAdpf, onError, ratingAddressRegionCode, year, yearOptions]
  );

  useEffect(
    function fetchModels() {
      let isActive = true;
      if (
        yearOptions.map((opt) => opt.value).includes(yearRef.current || '') &&
        makeOptions.map(getLowerCaseValue).includes(make?.toLowerCase()) &&
        !isAdpf
      ) {
        makeRef.current = make;
        api
          .getVehiclesModels(
            yearRef.current ? yearRef.current : '',
            make.toUpperCase(),
            ratingAddressRegionCode,
            effectiveDate
          )
          .then((models) => {
            if (isActive) {
              setModelOptions(
                models.map((modelOpt) => ({
                  label: modelOpt.model,
                  value: modelOpt.model,
                  styles: modelOpt.styles,
                }))
              );
            }
          })
          .catch(onError);
      }
      return () => (isActive = false);
    } as () => void,
    [isAdpf, make, makeOptions, onError, ratingAddressRegionCode, yearOptions]
  );

  useEffect(
    function setBodyStyles() {
      if (
        yearOptions.map((opt) => opt.value).includes(yearRef?.current || '') &&
        makeOptions
          .map(getLowerCaseValue)
          .includes(makeRef.current?.toLowerCase() || '') &&
        modelOptions
          .map(getLowerCaseValue)
          .includes(model?.toLowerCase() || '') &&
        modelRef.current !== model
      ) {
        modelRef.current = model;

        const currentModel = modelOptions.find(
          (modelOpt) => getLowerCaseValue(modelOpt) === model?.toLowerCase()
        );

        if (isApiV1) {
          setBodyStyleOptions(
            ((currentModel?.styles as string[]) || []).map((style: string) => ({
              label: style,
              value: style,
            }))
          );
        } else {
          setBodyStyleOptions(
            ((currentModel?.styles as { style: string }[]) || []).map(
              ({ style }: { style: string }) => ({
                label: style,
                value: style,
              })
            )
          );
        }
      }
    },
    [makeOptions, model, modelOptions, yearOptions]
  );

  useEffect(function setTrim() {
    if (
      !isApiV1 &&
      yearOptions.map((opt) => opt.value).includes(yearRef.current || '') &&
      makeOptions
        .map(getLowerCaseValue)
        .includes(makeRef.current?.toLowerCase() || '') &&
      modelOptions.map(getLowerCaseValue).includes(model.toLowerCase()) &&
      bodyStyleOptions
        .map(getLowerCaseValue)
        .includes(bodyStyle?.toLowerCase() || '') &&
      bodyStyleRef.current !== bodyStyle
    ) {
      bodyStyleRef.current = bodyStyle;

      const currentModel = modelOptions.find(
        (modelOpt) => getLowerCaseValue(modelOpt) === model?.toLowerCase()
      );

      const currentStyle = (
        (currentModel?.styles as { style: string; trims: string[] }[]) || []
      ).find(({ style }) => style?.toLowerCase() === bodyStyle?.toLowerCase());

      setTrimOptions(
        (currentStyle?.trims || []).map((trim) => ({
          value: trim,
          label: trim,
        }))
      );
    }
  });

  return {
    yearOptions,
    makeOptions,
    modelOptions,
    bodyStyleOptions,
    ...(!isApiV1 && {
      trimOptions,
    }),
  };
}
